import React, { useMemo, useState } from "react";
import { Bookmark, CalendarDays, FileText, Loader2, PenLine, Play, Share2, Users, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import SessionPlanOutline from "../workout/SessionPlanOutline";
import SlotGuide from "./SlotGuide";
import { Group, ItemRow } from "../profile/ProfileParts";
import { getSlotRecommendation } from "../../config/periodisation";
import { coerceLegacyNodeTypes } from "../../utils/planNormaliser";
import { copySummaryToClipboard } from "../../utils/sessionShare";
import { useToast } from "../../contexts/ToastContext";
import ShareLinkDialog from "../share/ShareLinkDialog";
import {
  builtName,
  formatSessionDate,
  slotName,
  slotPosition,
  slotWeekday,
  weekDayDate,
} from "../../utils/programmeSchedule";
import { mateName, matesBuilds } from "../../hooks/useProgrammeMates";

const ghost =
  "h-12 w-full border-border bg-muted/40 text-base font-semibold hover:bg-muted/60";

/**
 * One sheet for any programme session, whatever its state:
 *  - not built: what the slot asks for, then Build, or generate with AI,
 *    write manually, or use a saved session (#173)
 *  - built: the plan, then Start, Edit or Rebuild
 *  - done: what you did, with your effort and notes
 *  - missed: a note that the programme simply carries on
 */
const ProgrammeSessionSheet = ({
  open,
  onOpenChange,
  programme,
  session,
  onStart,
  onEdit,
  onRebuild,
  onBuild,
  onCustomise,
  onWrite,
  onUseSaved,
  // The week pool (#196).
  onMove,
  // Training together (#184).
  group = null,
  me = null,
  onUseMateBuild,
  onAdapt,
  adapting = false,
}) => {
  const imported = programme?.source === "imported";
  const rec = useMemo(
    () => getSlotRecommendation(programme, session),
    [programme, session],
  );
  // A done slot shows the session as it was done (#192): the plan could be
  // changed before Start and during the run, and Activity shows that version.
  const plan = useMemo(() => {
    const source =
      session?.status === "completed" && session.session_plan_data
        ? session.session_plan_data
        : session?.plan_data;
    return source ? coerceLegacyNodeTypes(JSON.parse(JSON.stringify(source))) : null;
  }, [session]);
  const { showToast } = useToast();
  const [shareOpen, setShareOpen] = useState(false);
  if (!session) return null;

  // Copy or send the plan to a friend (#183). The summary is the readable
  // version; the link gives them a copy they can load and run.
  const sharePlan = plan && (
    <Group title="Share">
      <ItemRow
        Icon={FileText}
        title="Copy summary"
        detail="Readable list to paste in a message"
        onClick={async () => {
          const ok = await copySummaryToClipboard(plan);
          showToast(ok ? { title: "Summary copied" } : { title: "Couldn't copy", variant: "destructive" });
        }}
      />
      <ItemRow
        Icon={Share2}
        title="Share a link"
        detail="A link to a copy of this session"
        onClick={() => setShareOpen(true)}
      />
    </Group>
  );

  const { index, count } = slotPosition(programme, session);
  const where = `Week ${session.week_number}${index ? ` · session ${index} of ${count}` : ""}`;
  const name = builtName(session);
  const status = session.status;
  const built = Boolean(session.plan_data);
  // A session someone else in the group built: say whose, and offer to
  // re-fit it to your kit. Adapting makes your copy your own.
  const builder =
    group && built && session.built_by && Number(session.built_by) !== Number(me)
      ? mateName(group, session.built_by)
      : null;
  const offers = group && !built && status === "pending" ? matesBuilds(group, session) : [];
  const followNote = group?.follows ? " Your copy then stops following the group's version." : "";
  const adaptButton = (plan) => (
    <Button
      type="button"
      variant="outline"
      className={ghost}
      disabled={adapting}
      onClick={() => onAdapt?.(session, plan)}
    >
      {adapting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
      Adapt for me
    </Button>
  );
  // Why this day differs from the usual plan, from the notes review (#92).
  const planNote = session.plan_note ? (
    <p className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm leading-relaxed text-foreground">
      {session.plan_note}
    </p>
  ) : null;

  let description;
  let body;
  let footer;

  if (status === "completed") {
    const date = formatSessionDate(session.completed_at, { weekday: true });
    const facts = [
      date && `Done ${date}`,
      session.duration_minutes && `${session.duration_minutes} min`,
      session.session_rating != null && `Effort ${session.session_rating}/10`,
    ].filter(Boolean);
    description = [name, where].filter(Boolean).join(" · ");
    body = (
      <>
        <div className="space-y-1 rounded-xl border border-secondary/40 bg-secondary/10 px-3 py-2.5 text-sm">
          <p className="font-semibold text-foreground">{facts.join(" · ")}</p>
          {session.session_notes && (
            <p className="italic text-muted-foreground">
              {`“${session.session_notes}”`}
            </p>
          )}
        </div>
        {plan && <SessionPlanOutline plan={plan} maxHeight="" />}
        {sharePlan}
      </>
    );
  } else if (status === "missed") {
    description = `Missed · ${where}`;
    body = (
      <p className="text-sm leading-relaxed text-muted-foreground">
        Missed sessions stay in the record. The programme carries on from
        where you are, so there's nothing to catch up.
      </p>
    );
  } else if (built) {
    description = [name, where, rec?.durationMin && `${rec.durationMin} min`]
      .filter(Boolean)
      .join(" · ");
    body = (
      <>
        {planNote}
        {/* The plan is the detail here, so the slot's targets shrink to a
            line and the sheet scrolls as one rather than in a small window
            (#183). */}
        {builder && (
          <p className="flex items-start gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-sm leading-relaxed text-foreground">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span>
              {`Built by ${builder}. `}
              <span className="text-muted-foreground">{`Adapt for me swaps anything your kit can't do.${followNote}`}</span>
            </span>
          </p>
        )}
        {!imported && <SlotGuide rec={rec} compact />}
        {plan && <SessionPlanOutline plan={plan} maxHeight="" />}
        {sharePlan}
      </>
    );
    footer = (
      <div className="grid gap-2">
        {builder && adaptButton(session.plan_data)}
        <Button
          type="button"
          className="h-12 w-full text-base font-bold"
          onClick={() => onStart?.(session)}
        >
          <Play className="mr-2 h-4 w-4 fill-current" />
          Start
        </Button>
        <div className={imported ? "grid gap-2" : "grid grid-cols-2 gap-2"}>
          <Button
            type="button"
            variant="outline"
            className={ghost}
            onClick={() => onEdit?.(session)}
          >
            Edit
          </Button>
          {/* An imported session is as written: nothing to rebuild it from. */}
          {!imported && (
            <Button
              type="button"
              variant="outline"
              className={ghost}
              onClick={() => onRebuild?.(session)}
            >
              Rebuild
            </Button>
          )}
        </div>
      </div>
    );
  } else {
    description = `Not built yet · ${where}`;
    body = (
      <>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Khronos builds this session from your programme: what this slot asks
          for, how the same slot went last week, and the rest of your week.
        </p>
        {planNote}
        {offers.slice(0, 1).map((b) => (
          <div key={b.user_id} className="grid gap-2 rounded-xl border border-primary/40 bg-primary/[0.06] p-3">
            <p className="text-sm font-semibold text-foreground">
              {`${b.name.split(" ")[0]} built this one${b.label ? `: ${b.label}` : ""}`}
            </p>
            <SessionPlanOutline
              plan={coerceLegacyNodeTypes(JSON.parse(JSON.stringify(b.plan_data)))}
              maxHeight="max-h-[30vh]"
            />
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" className="h-12 w-full font-semibold" onClick={() => onUseMateBuild?.(session, b)}>
                Use it
              </Button>
              {adaptButton(b.plan_data)}
            </div>
            {followNote && <p className="text-xs text-muted-foreground">{`Adapting swaps anything your kit can't do.${followNote}`}</p>}
          </div>
        ))}
        <SlotGuide rec={rec} />
        <Group title="Other ways">
          <ItemRow
            Icon={Wand2}
            title="Generate with AI"
            detail="Pick the time and kit, or tell it what you want"
            onClick={() => onCustomise?.(session)}
          />
          <ItemRow
            Icon={PenLine}
            title="Write manually"
            detail="No AI: type your exercises, e.g. bench 3x8 80"
            onClick={() => onWrite?.(session)}
          />
          <ItemRow
            Icon={Bookmark}
            title="Use a saved session"
            detail="One you've saved or done before"
            onClick={() => onUseSaved?.(session)}
          />
        </Group>
      </>
    );
    footer = (
      <Button
        type="button"
        className="h-12 w-full text-base font-bold"
        onClick={() => onBuild?.(session)}
      >
        Build this session
      </Button>
    );
  }

  // Not done yet: move it to another day, the pool, or skip it (#196).
  if (onMove && (status === "pending" || status === "skipped")) {
    const weekday = slotWeekday(programme, session);
    const date = weekday == null ? null : weekDayDate(programme, session.week_number, weekday);
    const when =
      status === "skipped"
        ? "Skipped this week"
        : date
          ? `On ${date.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}`
          : "Not on a day yet";
    body = (
      <>
        <Group title="Day">
          <ItemRow Icon={CalendarDays} title="Move, pool or skip" detail={when} onClick={() => onMove(session)} />
        </Group>
        {body}
      </>
    );
  }

  // A dialog can't sit above the bottom sheet, so the sheet steps aside
  // while the share dialog is open, as in Activity.
  return (
    <>
      <DetailSheet
        open={open && !shareOpen}
        onOpenChange={onOpenChange}
        title={slotName(session)}
        description={description}
        showDescription
        footer={footer}
      >
        {body}
      </DetailSheet>
      {plan && (
        <ShareLinkDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          request={{ kind: "plan", plan }}
          defaultTitle={plan.label || name || ""}
        />
      )}
    </>
  );
};

export default ProgrammeSessionSheet;
